import React from 'react';
import { Box, Typography, Paper, Tabs, Tab } from '@mui/material';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ProfessorContactMetrics from '../../components/dashboard/ProfessorContactMetrics';
import EmailMetrics from '../../components/dashboard/EmailMetrics';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`metrics-tabpanel-${index}`}
      aria-labelledby={`metrics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `metrics-tab-${index}`,
    'aria-controls': `metrics-tabpanel-${index}`,
  };
}

const ProfessorMetricsPage: React.FC = () => {
  const [value, setValue] = React.useState(0);
  const { user, logout } = useAuth();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleLogout = () => {
    logout();
    // Redirect happens automatically due to protected route
  };

  return (
    <div className="metrics-page">
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary">
        <div className="container">
          <Link className="navbar-brand" to="/dashboard">Professor Connect</Link>
          
          <button 
            className="navbar-toggler" 
            type="button" 
            data-bs-toggle="collapse" 
            data-bs-target="#navbarNav" 
            aria-controls="navbarNav" 
            aria-expanded="false" 
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav me-auto">
              <li className="nav-item">
                <Link className="nav-link" to="/dashboard">Dashboard</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/professors">Professors</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link active" to="/professor-metrics">Professor Metrics</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/outreach">My Outreach</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/templates">Email Templates</Link>
              </li>
            </ul>
            
            <div className="d-flex align-items-center">
              <Link to="/settings" className="btn btn-outline-light me-2">Settings</Link>
              
              <div className="dropdown">
                <button 
                  className="btn btn-outline-light dropdown-toggle" 
                  type="button" 
                  id="userDropdown" 
                  data-bs-toggle="dropdown" 
                  aria-expanded="false"
                >
                  {user?.firstName} {user?.lastName}
                </button>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
                  <li><Link className="dropdown-item" to="/profile">Profile</Link></li>
                  <li><Link className="dropdown-item" to="/settings">Settings</Link></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li><button className="dropdown-item" onClick={handleLogout}>Logout</button></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </nav>
      
      <div className="container py-5">
      <Typography variant="h4" component="h1" gutterBottom>
        Professor Contact Metrics
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Track your outreach effectiveness and monitor professor responses.
      </Typography>

      <Paper sx={{ mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={value} 
            onChange={handleChange} 
            aria-label="metrics tabs"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Contact Overview" {...a11yProps(0)} />
            <Tab label="Email Analytics" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>
          <ProfessorContactMetrics />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <EmailMetrics />
        </TabPanel>
      </Paper>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Tips for Improving Response Rates
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Box component="ul" sx={{ pl: 2 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography variant="body1">
                <strong>Personalize your emails</strong> - Reference specific research papers or projects the professor has worked on.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography variant="body1">
                <strong>Be concise</strong> - Keep your initial email under 300 words and clearly state your purpose.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography variant="body1">
                <strong>Show genuine interest</strong> - Demonstrate that you've read their work and have specific questions.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography variant="body1">
                <strong>Follow up appropriately</strong> - Wait 7-10 days before sending a polite follow-up email.
              </Typography>
            </Box>
            <Box component="li">
              <Typography variant="body1">
                <strong>Timing matters</strong> - Send emails during weekdays, preferably in the morning when professors are checking their inbox.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
      </div>
    </div>
  );
};

export default ProfessorMetricsPage;
